/**
 * Indian mobile number validator.
 *
 * Accepts: "+91 98765 43210", "+919876543210", "9876543210", "919876543210"
 * Returns: { valid: boolean, normalized: string|null, error: string|null }
 *
 * Rules enforced on the final 10-digit number:
 *   - Exactly 10 digits
 *   - First digit must be 6, 7, 8, or 9 (TRAI rule)
 *   - Rejects obvious fakes:
 *       • All same digit              → 9999999999, 8888888888
 *       • Strict ascending/descending → 0123456789, 9876543210, 1234567890
 *       • Repeating 2-digit pattern   → 9898989898, 1212121212
 *       • Repeating 3-digit pattern   → 123123123x style
 *       • Fewer than 4 unique digits  → covers 1111122222, 9090909090, etc.
 */
export const validateIndianMobile = (input) => {
  if (!input || typeof input !== 'string') {
    return { valid: false, normalized: null, error: 'Please enter your mobile number.' };
  }

  // Strip everything except digits
  let digits = input.replace(/\D+/g, '');

  // Drop leading country code if present
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);

  if (digits.length !== 10) {
    return { valid: false, normalized: null, error: 'Please enter a valid 10-digit mobile number.' };
  }

  if (!/^[6-9]/.test(digits)) {
    return { valid: false, normalized: null, error: 'Mobile number must start with 6, 7, 8 or 9.' };
  }

  // All same digit
  if (/^(\d)\1{9}$/.test(digits)) {
    return { valid: false, normalized: null, error: 'Please enter a valid mobile number.' };
  }

  // Strict ascending or descending sequences
  const ASC = '0123456789';
  const DESC = '9876543210';
  if (ASC.includes(digits) || DESC.includes(digits)) {
    return { valid: false, normalized: null, error: 'Please enter a valid mobile number.' };
  }

  // Repeating 2-digit pattern (e.g. 9898989898)
  if (/^(\d{2})\1{4}$/.test(digits)) {
    return { valid: false, normalized: null, error: 'Please enter a valid mobile number.' };
  }

  // Repeating 3-digit pattern at start (e.g. 123123123x)
  if (/^(\d{3})\1{2}\d$/.test(digits)) {
    return { valid: false, normalized: null, error: 'Please enter a valid mobile number.' };
  }

  // Fewer than 4 unique digits — too low entropy to be real
  const uniqueCount = new Set(digits).size;
  if (uniqueCount < 4) {
    return { valid: false, normalized: null, error: 'Please enter a valid mobile number.' };
  }

  return { valid: true, normalized: digits, error: null };
};
